import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, X, AlertTriangle, Smartphone, Wifi, WifiOff } from 'lucide-angular';

export interface ErrorDetails {
  statusCode: number;
  message: string;
  path?: string;
  method?: string;
  timestamp?: string;
  error?: string;
}

@Component({
  selector: 'app-error-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="modal-overlay" (click)="closeModal()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <div class="header-left">
            <lucide-icon [img]="AlertTriangleIcon" [size]="20" class="error-icon"></lucide-icon>
            <h2>Erro na Operação</h2>
          </div>
          <button class="close-btn" (click)="closeModal()">
            <lucide-icon [img]="XIcon" class="w-5 h-5"></lucide-icon>
          </button>
        </div>

        <div class="modal-body">
          <div class="error-content">
            <div class="error-icon-large">
              <lucide-icon [img]="getErrorIcon()" [size]="48" class="error-icon-main"></lucide-icon>
            </div>

            <div class="error-message">
              <h3>{{ getErrorMessage() }}</h3>
              <p>{{ getErrorDescription() }}</p>
            </div>

            <div class="error-details" *ngIf="showDetails">
              <div class="detail-item">
                <span class="detail-label">Código:</span>
                <span class="detail-value">{{ error?.statusCode || 'N/A' }}</span>
              </div>
              <div class="detail-item" *ngIf="error?.path">
                <span class="detail-label">Endpoint:</span>
                <span class="detail-value">{{ error?.method }} {{ error?.path }}</span>
              </div>
              <div class="detail-item" *ngIf="error?.timestamp">
                <span class="detail-label">Horário:</span>
                <span class="detail-value">{{ formatTimestamp(error?.timestamp) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="details-btn" (click)="toggleDetails()">
            {{ showDetails ? 'Ocultar Detalhes' : 'Ver Detalhes' }}
          </button>
          <button class="close-error-btn" (click)="closeModal()">Fechar</button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgb(17 24 39 / 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
      }

      .modal-content {
        background: white;
        border-radius: 8px;
        width: 90%;
        max-width: 500px;
        max-height: 80vh;
        overflow: hidden;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #e5e7eb;
        background-color: #f9fafb;
      }

      .header-left {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .error-icon {
        color: #dc2626;
      }

      .modal-header h2 {
        margin: 0;
        color: #1f2937;
        font-size: 1.25rem;
        font-weight: 600;
      }

      .close-btn {
        background: none;
        border: none;
        font-size: 1.25rem;
        color: #6b7280;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 0.25rem;
        transition: all 0.2s;
      }

      .close-btn:hover {
        color: #374151;
        background-color: #f3f4f6;
      }

      .modal-body {
        padding: 1.5rem;
      }

      .error-content {
        text-align: center;
      }

      .error-icon-large {
        margin-bottom: 1rem;
      }

      .error-icon-main {
        color: #dc2626;
      }

      .error-message h3 {
        margin: 0 0 0.5rem 0;
        color: #1f2937;
        font-size: 1.125rem;
        font-weight: 600;
      }

      .error-message p {
        margin: 0 0 1.5rem 0;
        color: #6b7280;
        line-height: 1.5;
      }

      .error-details {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        padding: 1rem;
        text-align: left;
      }

      .detail-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0;
        border-bottom: 1px solid #f3f4f6;
      }

      .detail-item:last-child {
        border-bottom: none;
      }

      .detail-label {
        font-weight: 500;
        color: #374151;
        font-size: 0.875rem;
      }

      .detail-value {
        color: #1f2937;
        font-size: 0.875rem;
        font-family: monospace;
      }

      .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        padding: 1rem 1.5rem;
        border-top: 1px solid #e5e7eb;
        background-color: #f9fafb;
      }

      .details-btn {
        padding: 0.5rem 1rem;
        border: 1px solid #d1d5db;
        background: #ffffff;
        color: #374151;
        border-radius: 0.375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .details-btn:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
      }

      .close-error-btn {
        padding: 0.5rem 1rem;
        border: none;
        background: #dc2626;
        color: #ffffff;
        border-radius: 0.375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .close-error-btn:hover {
        background: #b91c1c;
      }
    `,
  ],
})
export class ErrorModalComponent {
  @Input() error: ErrorDetails | null = null;
  @Output() close = new EventEmitter<void>();

  showDetails = false;

  // Icons
  readonly AlertTriangleIcon = AlertTriangle;
  readonly XIcon = X;
  readonly SmartphoneIcon = Smartphone;
  readonly WifiIcon = Wifi;
  readonly WifiOffIcon = WifiOff;

  getErrorIcon() {
    if (this.isFCMTokenError()) {
      return this.SmartphoneIcon;
    }
    if (this.isNetworkError()) {
      return this.WifiOffIcon;
    }
    return this.AlertTriangleIcon;
  }

  getErrorMessage(): string {
    if (this.isFCMTokenError()) {
      return 'Dispositivo sem Token FCM';
    }
    if (this.isNetworkError()) {
      return 'Erro de Conexão';
    }
    return 'Erro na Operação';
  }

  getErrorDescription(): string {
    if (this.isFCMTokenError()) {
      return 'O dispositivo selecionado não possui um token FCM registrado. Verifique se o aplicativo móvel está instalado e configurado corretamente.';
    }
    if (this.isNetworkError()) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.';
    }
    return this.error?.message || 'Ocorreu um erro inesperado. Tente novamente.';
  }

  private isFCMTokenError(): boolean {
    return (
      (this.error?.message?.includes('FCM token') ?? false) ||
      (this.error?.message?.includes('token registrado') ?? false) ||
      this.error?.statusCode === 400
    );
  }

  private isNetworkError(): boolean {
    return (
      this.error?.statusCode === 0 ||
      this.error?.statusCode === 500 ||
      (this.error?.message?.includes('Network') ?? false) ||
      (this.error?.message?.includes('conexão') ?? false)
    );
  }

  toggleDetails(): void {
    this.showDetails = !this.showDetails;
  }

  formatTimestamp(timestamp: string | undefined): string {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return timestamp;
    }
  }

  closeModal(): void {
    this.close.emit();
  }
}
